import type { Lifecycle } from "@/types/lifecycle";

import { createId, createPastTimestamp } from "./base";

interface LifecycleConfig {
  name: string;
  emoji: string;
  stage: number;
  isDefault: boolean;
  isWon?: boolean;
  isLost?: boolean;
  isSpam?: boolean;
  type: "lead" | "lost";
}

const CLINIC_LIFECYCLES: LifecycleConfig[] = [
  { name: "New Lead", emoji: "👋", stage: 1, isDefault: true, type: "lead" },
  { name: "Contacted", emoji: "📞", stage: 2, isDefault: false, type: "lead" },
  {
    name: "Consultation Scheduled",
    emoji: "📅",
    stage: 3,
    isDefault: false,
    type: "lead",
  },
  {
    name: "Treatment Planned",
    emoji: "📋",
    stage: 4,
    isDefault: false,
    type: "lead",
  },
  {
    name: "Patient",
    emoji: "✅",
    stage: 5,
    isDefault: false,
    isWon: true,
    type: "lead",
  },
  {
    name: "Lost",
    emoji: "❌",
    stage: 6,
    isDefault: false,
    isLost: true,
    type: "lost",
  },
  {
    name: "Spam",
    emoji: "🚫",
    stage: 7,
    isDefault: false,
    isSpam: true,
    type: "lost",
  },
];

/**
 * Generate lifecycle stages for a clinic
 */
export function generateLifecycles(
  organizationId: string,
  workspaceId: string,
): Lifecycle[] {
  const timestamp = createPastTimestamp(90);

  return CLINIC_LIFECYCLES.map((config) => ({
    id: createId(),
    name: config.name,
    emoji: config.emoji,
    stage: config.stage,
    isDefault: config.isDefault,
    isWon: config.isWon,
    isLost: config.isLost,
    isSpam: config.isSpam,
    type: config.type,
    organizationId,
    workspaceId,
    createdAt: timestamp,
    updatedAt: timestamp,
    contactCount: 0, // Will be updated when contacts are generated
  }));
}

/**
 * Get lifecycle by stage
 */
export function getLifecycleByStage(
  lifecycles: Lifecycle[],
  stage: number,
): Lifecycle | undefined {
  return lifecycles.find((l) => l.stage === stage);
}

/**
 * Get default lifecycle
 */
export function getDefaultLifecycle(
  lifecycles: Lifecycle[],
): Lifecycle | undefined {
  return lifecycles.find((l) => l.isDefault);
}
